import React, { useContext, useState } from "react";
import { CorporateDashboardContext, initialState } from "./CorporateDashboardContext";
import {
    ICorporateDashboardContextProps,
    ISelectedFailure,
    ISelectedMachineCenterPopoverData,
    ISelectedMillPopoverData,
} from "./CorporateDashboardContextInterfaces";
import {
    ICorporateDashboardFilters,
    validCorporateSummaryDefaultFields,
} from "pages/Corporate/CorporateDashboard/useCorporateDashboardFilters";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { UsersEntity } from "entities/Users/UsersEntity";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { filterObjectProperties } from "utilities/utilities";
import { useTranslation } from "react-i18next";
import { Alert, Snackbar } from "@mui/material";

export const CorporateDashboardContextProvider: React.FC<ICorporateDashboardContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filters, setFilters] = useState(initialState.filters);
    const [selectedFailureType, setSelectedFailureType] = useState<ISelectedFailure | undefined>(undefined);
    const [selectedFailureCode, setSelectedFailureCode] = useState<ISelectedFailure | undefined>(undefined);
    const [selectedSecondaryFailureCode, setSelectedSecondaryFailureCode] = useState<ISelectedFailure | undefined>(
        undefined,
    );
    const [totalDowntimeSeconds, setTotalDowntimeSeconds] = useState<number | undefined>(undefined);
    const [corporationCountryCode, setCorporationCountryCode] = useState<string>("");
    const [userFiltersDefault, setUserFiltersDefault] = useState<ICorporateDashboardFilters | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedMillPopoverData, setSelectedMillPopoverData] = useState<ISelectedMillPopoverData | null>(null);
    const [selectedMachineCenterPopoverData, setSelectedMachineCenterPopoverData] =
        useState<ISelectedMachineCenterPopoverData | null>(null);

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault: ICorporateDashboardFilters = JSON.parse(userFilterPreference[0].filter_selection);
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.CORPORATE_DASHBOARD_SUMMARY,
        currentUser?.user_id,
    );

    const showOrHideFilters = (value: boolean) => {
        setShowFilters(value);
    };

    const handleSetTotalDowntimeSeconds = (value: number) => {
        setTotalDowntimeSeconds(value);
    };

    const handleSetFilters = (value: ICorporateDashboardFilters) => {
        setFilters(value);
        setSelectedMillPopoverData(null);
        setSelectedMachineCenterPopoverData(null);
    };

    const handleSetSelectedMillPopoverData = (data: ISelectedMillPopoverData | null) => {
        setSelectedMillPopoverData(data);
        if (!data) {
            setSelectedMachineCenterPopoverData(null);
        }
    };

    const handleSetSelectedMachineCenterPopoverData = (data: ISelectedMachineCenterPopoverData | null) => {
        setSelectedMachineCenterPopoverData(data);
    };

    const handleSetSelectedFailureType = (value: ISelectedFailure | undefined) => {
        if (value?.id === selectedFailureType?.id) return;
        setSelectedFailureType(value);
        setSelectedFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
    };

    const handleSetSelectedFailureCode = (value: ISelectedFailure | undefined) => {
        if (value?.id === selectedFailureCode?.id) return;
        setSelectedFailureCode(value);
        setSelectedSecondaryFailureCode(undefined);
    };

    const handleSetSelectedSecondaryFailureCode = (value: ISelectedFailure | undefined) => {
        setSelectedSecondaryFailureCode(value);
    };

    const resetSelections = () => {
        setSelectedFailureType(undefined);
        setSelectedFailureCode(undefined);
        setSelectedSecondaryFailureCode(undefined);
        setSelectedMillPopoverData(null);
        setSelectedMachineCenterPopoverData(null);
    };

    const handleSetCorporationCountryCode = (countryCode: string) => {
        setCorporationCountryCode(countryCode);
    };

    const handleSetFiltersDefault = async (summaryFilters: ICorporateDashboardFilters) => {
        try {
            const validFilters = filterObjectProperties(summaryFilters, validCorporateSummaryDefaultFields);
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.CORPORATE_DASHBOARD_SUMMARY,
                        filter_selection: JSON.stringify(validFilters),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.CORPORATE_DASHBOARD_SUMMARY,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const providerValue = {
        filters,
        showFilters,
        totalDowntimeSeconds,
        handleSetTotalDowntimeSeconds,
        showOrHideFilters,
        handleSetFilters,
        handleSetSelectedFailureType,
        selectedFailureType,
        handleSetSelectedFailureCode,
        selectedFailureCode,
        handleSetSelectedSecondaryFailureCode,
        selectedSecondaryFailureCode,
        resetSelections,
        corporationCountryCode,
        handleSetCorporationCountryCode,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
        selectedMillPopoverData,
        selectedMachineCenterPopoverData,
        handleSetSelectedMillPopoverData,
        handleSetSelectedMachineCenterPopoverData,
    };

    return (
        <CorporateDashboardContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </CorporateDashboardContext.Provider>
    );
};
